import React, { useRef } from 'react'
import styled from 'styled-components'
import { Form, Icon } from 'components/base-ui'
import { required } from 'helpers/validators'
import { useTranslation } from 'react-i18next'
import { CreditCardBrandEnum } from 'enums'
import { cloneObject } from 'helpers'
import CreditCardImage from './CreditCardImage'

let onBlurTimer: ReturnType<typeof setTimeout>

export interface CreditCard {
  cardNumber: string
  cardHolderName: string
  expireDate: string
  cvc: string
}

const initialFormData: CreditCard = {
  cardNumber: '',
  cardHolderName: '',
  expireDate: '',
  cvc: '',
}

interface FormCreditCardProps {
  formCreditData: any
  setAsyncInitData: any
  handleOnChange: any
  creditCardBrand: string
  onDetectBrandCreditCard?: (brand: string) => void
  onUpdatedForm?: () => void
  onResetForm?: () => void
  haveOmiseCreditCard?: boolean
  isSavedCreditCard?: boolean
  refForm?: any
  refCardNumber?: any
  refCardHolderName?: any
  refExpireDate?: any
  refCVCInput?: any
  errorMsgCreditCard?: string
  formID: string
  onFocus?: () => void
  onBlur?: () => void
}

const FormCreditCard: React.FC<FormCreditCardProps> = ({
  formCreditData,
  handleOnChange,
  setAsyncInitData,
  creditCardBrand,
  haveOmiseCreditCard,
  isSavedCreditCard,
  onUpdatedForm,
  onResetForm,
  refForm,
  refCardNumber,
  refCardHolderName,
  refExpireDate,
  refCVCInput,
  errorMsgCreditCard,
  formID,
  onDetectBrandCreditCard,
  onFocus = () => {},
  onBlur = () => {},
}) => {
  const { t } = useTranslation()

  const _refForm = useRef<any>(null)
  const _refCardNumber = useRef(null)
  const _refCardHolderName = useRef(null)
  const _refExpireDate = useRef(null)
  const _refCVCInput = useRef(null)

  return (
    <>
      {errorMsgCreditCard && (
        <LabelError>
          <Icon name="notice-circle-2" />
          <div>{errorMsgCreditCard}</div>
        </LabelError>
      )}

      <Form
        id={formID}
        onFocus={onFocus}
        onBlur={onBlur}
        ref={(el) => {
          if (refForm) {
            refForm.current = el
          }
          if (_refForm) {
            _refForm.current = el
          }
        }}
      >
        <InputWrapper>
          <Form.Input
            id="input-card-number"
            ref={(el) => {
              if (refCardNumber) {
                refCardNumber.current = el
              }

              if (_refCardNumber) {
                _refCardNumber.current = el
              }
            }}
            type="text"
            inputMode="numeric"
            placeholder={t('cardNumberPlaceholder')}
            required
            validations={[required]}
            value={formCreditData.cardNumber}
            onChange={handleOnChange('cardNumber')}
            onKeyUp={(e) => handleChangeCreditCard(e, 'cardNumber')}
            onFocus={handleOnFocusInput}
            onBlur={handleOnBlurInput}
            maxLength="19"
          />
          <CardType>
            <CreditCardImage creditCardBrand={creditCardBrand} />
          </CardType>
        </InputWrapper>
        <Form.Input
          id="input-card-holder"
          ref={(el) => {
            if (refCardHolderName) {
              refCardHolderName.current = el
            }

            if (_refCardHolderName) {
              _refCardHolderName.current = el
            }
          }}
          type="text"
          placeholder={t('cardHolderNamePlaceholder')}
          required
          validations={[required]}
          value={formCreditData.cardHolderName}
          onChange={(e) => handleChangeCreditCard(e, 'cardHolderName')}
          onFocus={handleOnFocusInput}
          onBlur={handleOnBlurInput}
        />
        <InputWrapperTwoColumn>
          <Form.Input
            id="input-card-expire-date"
            ref={(el) => {
              if (refExpireDate) {
                refExpireDate.current = el
              }

              if (_refExpireDate) {
                _refExpireDate.current = el
              }
            }}
            type="text"
            inputMode="numeric"
            placeholder={t('cardExpireDatePlaceholder')}
            required
            validations={[required]}
            value={formCreditData.expireDate}
            onChange={handleOnChange('expireDate')}
            onKeyUp={(e) => handleChangeCreditCard(e, 'expireDate')}
            onFocus={handleOnFocusInput}
            onBlur={handleOnBlurInput}
            maxLength="5"
          />
          <Form.Input
            id="input-card-cvc"
            ref={(el) => {
              if (refCVCInput) {
                refCVCInput.current = el
              }

              if (_refCVCInput) {
                _refCVCInput.current = el
              }
            }}
            type="text"
            inputMode="numeric"
            placeholder={t('cardCVCPlaceholder')}
            required
            validations={[required]}
            placeholderSm
            value={formCreditData.cvc}
            onChange={(e) => handleChangeCreditCard(e, 'cvc')}
            onFocus={handleOnFocusInput}
            onBlur={handleOnBlurInput}
            maxLength="3"
          />
        </InputWrapperTwoColumn>
      </Form>
    </>
  )

  function resetFormState() {
    if (_refForm?.current) {
      _refForm.current.hideError(_refCardNumber.current)
      _refForm.current.hideError(_refCardHolderName.current)
      _refForm.current.hideError(_refExpireDate.current)
      _refForm.current.hideError(_refCVCInput.current)
    }
  }

  function handleOnFocusInput() {
    // For Clear IOS Issues overlapping event on LIFF
    clearTimeout(onBlurTimer)
  }

  function handleOnBlurInput() {
    // For Clear IOS Issues overlapping event on LIFF
    clearTimeout(onBlurTimer)
    onBlurTimer = setTimeout(() => {
      window.scrollTo(0, 0)
    }, 350)
  }

  function handleChangeCreditCard(e, fieldName) {
    // Reset ErrorMsg
    onUpdatedForm && onUpdatedForm()
    if (haveOmiseCreditCard && isSavedCreditCard) {
      // Clear Credit Data when change data
      const newValue =
        formCreditData[fieldName].length > e.target.value.length
          ? ''
          : e.target.value.replace(formCreditData[fieldName], '')
      const newValueFormData = cloneObject(initialFormData)
      newValueFormData[fieldName] = newValue
      resetFormState()
      setAsyncInitData(newValueFormData)
      onResetForm && onResetForm()
    } else {
      const key = e.keyCode || e.charCode

      // CardNumber Magic Auto Fill
      // ========================================
      if (fieldName === 'cardNumber' && /^\s/i.test(e.target.value)) {
        e.target.value = e.target.value.trim()
      }

      if (
        fieldName === 'cardNumber' &&
        e.target.value.replace(/ /g, '').length % 4 === 0 &&
        e.target.value[e.target.value.length - 1] !== ' ' &&
        e.target.value.replace(/ /g, '').length < 16 &&
        key !== 8
      ) {
        // auto fill space
        e.target.value += ' '
      }

      if (
        fieldName === 'cardNumber' &&
        e.target.value.length % 5 === 0 &&
        e.target.value[e.target.value.length - 1] !== ' ' &&
        key !== 8
      ) {
        // auto fill space before lastNumber
        const frontNumber = e.target.value.slice(0, e.target.value.length - 1)
        const lastNumber = e.target.value.slice(-1)
        e.target.value = `${frontNumber} ${lastNumber}`
      }

      if (fieldName === 'cardNumber') {
        // Detect Visa or Master Card Type
        const visaPattern = /^4/
        const masterCardPattern = /^5[1-5]/
        const JCBPattern = /^35/

        if (visaPattern.test(formCreditData.cardNumber)) {
          onDetectBrandCreditCard && onDetectBrandCreditCard(CreditCardBrandEnum.Visa)
        } else if (masterCardPattern.test(formCreditData.cardNumber)) {
          onDetectBrandCreditCard && onDetectBrandCreditCard(CreditCardBrandEnum.MasterCard)
        } else if (JCBPattern.test(formCreditData.cardNumber)) {
          onDetectBrandCreditCard && onDetectBrandCreditCard(CreditCardBrandEnum.JCB)
        } else {
          onDetectBrandCreditCard && onDetectBrandCreditCard('')
        }
      }
      // ========================================

      // ExpireDate Magic Auto Fill
      // ========================================
      if (fieldName === 'expireDate' && e.target.value.length === 2 && key !== 8) {
        // backspace
        e.target.value += '/'
      }

      if (
        fieldName === 'expireDate' &&
        e.target.value.length === 3 &&
        e.target.value[e.target.value.length - 1] !== '/' &&
        key !== 8
      ) {
        // backspace
        const frontNumber = e.target.value.slice(0, e.target.value.length - 1)
        const lastNumber = e.target.value.slice(-1)
        e.target.value = `${frontNumber}/${lastNumber}`
      }
      // ========================================

      handleOnChange(fieldName)(e)
    }
  }
}

export default FormCreditCard

export const InputWrapper = styled.div`
  display: flex;
  position: relative;
`

export const CardType = styled.div`
  position: absolute;
  top: 50%;
  margin-top: -8px;
  right: 15px;
  transform: translate3d(0, -50%, 0);
`

const InputWrapperTwoColumn = styled.div`
  display: flex;
  margin-left: -8px;
  margin-right: -8px;

  > div {
    margin: 0 8px;
  }
`

const LabelError = styled.div`
  margin-bottom: 15px;
  display: flex;
  color: ${(props) => props.theme.colors.red700};
  font-size: 12px;
  border-radius: 8px;
  padding: 9px 11px;
  box-shadow: 0 0 0 1px ${(props) => props.theme.colors.red700};
  background-color: ${(props) => props.theme.colors.red100};

  > div:first-letter {
    text-transform: capitalize;
  }

  i {
    position: relative;
    top: 2px;
    font-size: 13px;
    margin-right: 9px;
  }
`
